import React, { useState } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';

const GownRequestPage = () => {
  const [adminNumber, setAdminNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [graduate, setGraduate] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);

  const handleSearch = async () => {
    if (!adminNumber.trim()) {
      toast.error('Please enter your admission number');
      return;
    }

    setLoading(true);
    setGraduate(null);
    setRequestStatus(null);

    try {
      const response = await api.get(`/graduates/search?query=${encodeURIComponent(adminNumber)}`);

      if (response.data) {
        // Get active graduation day to check if graduation is upcoming
        let activeGraduationDay = null;
        try {
          const activeDayRes = await api.get('/graduation-days/active');
          activeGraduationDay = activeDayRes.data;
        } catch (err) {
          console.error('Error fetching active graduation day:', err);
        }

        // Calculate if graduation has passed
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        let ceremonyDate = null;
        if (response.data.ceremony_date) {
          ceremonyDate = new Date(response.data.ceremony_date);
          ceremonyDate.setHours(0, 0, 0, 0);
        } else if (activeGraduationDay && activeGraduationDay.ceremony_date) {
          ceremonyDate = new Date(activeGraduationDay.ceremony_date);
          ceremonyDate.setHours(0, 0, 0, 0);
        }

        const isGraduationPast = ceremonyDate && ceremonyDate < today;
        
        setGraduate({
          ...response.data,
          isGraduationPast,
          ceremonyDate
        });

        // Check if they have a gown request
        try {
          const gownCheck = await api.get(`/gowns/check-request/${response.data.id}`);
          if (gownCheck.data.hasRequest) {
            setRequestStatus(gownCheck.data.status);
          }
        } catch (gownError) {
          console.error('Error checking gown request:', gownError);
        }
      } else {
        toast.error('No graduate found with this admission');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('No graduate found with this admission number');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestGown = async () => {
    if (!graduate) return;

    setRequesting(true);
    try {
      const response = await api.post('/gowns/request', {
        graduate_id: graduate.id,
        gown_size: null
      });

      if (response.data.success) {
        setRequestStatus('pending');
        toast.success(response.data.message || 'Gown request submitted successfully! Please wait for admin approval.');
      } else {
        toast.error(response.data.message || 'Failed to submit gown request');
      }
    } catch (error) {
      console.error('Request error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit gown request');
    } finally {
      setRequesting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return { text: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800' };
      case 'approved':
        return { text: 'Approved - Ready for Collection', color: 'bg-green-100 text-green-800' };
      case 'issued':
        return { text: 'Issued', color: 'bg-blue-100 text-blue-800' };
      case 'returned':
        return { text: 'Returned', color: 'bg-gray-100 text-gray-800' };
      default:
        return { text: 'Not Requested', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div style={{ backgroundColor: '#FEFBEC', minHeight: '100vh' }}>
      {/* Hero Section - Left Aligned with Gold Bottom Border */}
      <div className="bg-white border-b-4 border-[#fbbf24] shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-xl">👗</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800 mb-0.5">
                Request Graduation Gown
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Enter your admission number to request your graduation gown
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto">
          <Card>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admission Number
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="e.g., DEE/001/S2021"
                      value={adminNumber}
                      onChange={(e) => setAdminNumber(e.target.value)}
                      className="w-full py-2 px-3"
                      style={{ height: '42px' }}
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={loading}
                    className="whitespace-nowrap px-4 py-2 text-sm"
                    style={{ height: '42px', display: 'inline-flex', alignItems: 'center' }}
                  >
                    {loading ? 'Searching...' : 'Verify'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter the admission number on your graduation card
                </p>
              </div>

              {graduate && (
                <div className="border-t pt-3">
                  <h3 className="text-base font-semibold text-gray-800 mb-3">Graduate Details</h3>

                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                    <div className="flex flex-col sm:flex-row sm:justify-between py-1">
                      <span className="font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Name:</span>
                      <span className="text-gray-900 sm:w-2/3 break-words">{graduate.name}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between py-1">
                      <span className="font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Admin Number:</span>
                      <span className="text-gray-900 font-mono sm:w-2/3 break-words">{graduate.admin_number}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between py-1">
                      <span className="font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Course:</span>
                      <span className="text-gray-900 sm:w-2/3 break-words">
                        {graduate.course_name} {graduate.level_number ? `(Level ${graduate.level_number})` : graduate.level_name ? `(${graduate.level_name})` : ''}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between py-1">
                      <span className="font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Department:</span>
                      <span className="text-gray-900 sm:w-2/3 break-words">{graduate.department_name}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between py-1">
                      <span className="font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Graduation Year:</span>
                      <span className="text-gray-900 sm:w-2/3 break-words">{graduate.graduation_year || '—'}</span>
                    </div>
                    {graduate.ceremonyDate && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-1">
                        <span className="font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3">Ceremony Date:</span>
                        <span className="text-gray-900 sm:w-2/3 break-words">{formatDate(graduate.ceremonyDate)}</span>
                      </div>
                    )}
                  </div>

                  {/* Show graduation passed message */}
                  {graduate.isGraduationPast && (
                    <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
                      <div className="flex items-center gap-2">
                        <span className="text-red-600 text-lg">⚠️</span>
                        <div>
                          <p className="text-sm text-red-800 font-medium">
                            Graduation Has Passed
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            Gown requests are only available for upcoming graduations. 
                            Please contact the registrar's office for assistance.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {requestStatus ? (
                    <div className="mt-3 p-3 rounded-lg bg-gray-50">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <span className="font-medium text-gray-700">Request Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold inline-block text-center ${getStatusBadge(requestStatus).color}`}>
                          {getStatusBadge(requestStatus).text}
                        </span>
                      </div>
                      {requestStatus === 'approved' && (
                        <div className="mt-2 text-xs text-green-600">
                          ✓ Your gown is ready for collection. Please visit the registrar's office.
                        </div>
                      )}
                      {requestStatus === 'issued' && (
                        <div className="mt-2 text-xs text-blue-600">
                          📍 Your gown has been issued. Please return it after the ceremony.
                        </div>
                      )}
                      {requestStatus === 'returned' && (
                        <div className="mt-2 text-xs text-gray-600">
                          ✓ Gown has been returned. Thank you!
                        </div>
                      )}
                      {requestStatus === 'pending' && (
                        <div className="mt-2 text-xs text-yellow-600">
                          ⏳ Your request is pending approval. You will be notified once approved.
                        </div>
                      )}
                    </div>
                  ) : (
                    !graduate.isGraduationPast && (
                      <div className="mt-3">
                        <Button
                          onClick={handleRequestGown}
                          disabled={requesting}
                          className="w-full py-2 text-sm"
                          variant="primary"
                        >
                          {requesting ? 'Submitting...' : 'Request Gown'}
                        </Button>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          Request will be processed by the admin. You will receive updates via SMS.
                        </p>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </Card>

          <div className="mt-6 text-center text-xs text-gray-500">
            <p>For inquiries, contact the registrar's office or call +254719 508 039</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GownRequestPage;
